import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    BulkSelectEmployeeDialogComponent,
    ConfirmationDialogComponent,
    DeprecatedModalComponent,
} from '@app/components';
import { AnalyticEvents } from '@app/enums/analytic-events.enum';
import { Employee } from '@app/models/employee/employee.model';
import { Task } from '@app/models/tasks/task.model';
import { TaskDetailsComponent } from '@app/modules/employees/components/task-details/task-details.component';
import { AssignTaskTemplatesService, TaskTemplateCompanyTasksTable } from '@app/modules/employees/services';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { TitlecasePipe } from '@app/pipes';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { Translatable } from '@app/types/translatable.type';
import { RoutingStateService } from '@common/services/routing-state.service';
import { TaskTemplate } from '@models/tasks/task-template.model';

@Component({
    templateUrl: './task-templates-show.template.html',
    styleUrls: ['./task-templates-show.style.scss'],
})
export class TaskTemplatesShowView implements OnInit {
    @ViewChild('confirmDeleteModal', { static: true })
    confirmDeleteModal: ConfirmationDialogComponent;
    @ViewChild('taskDetails', { static: true }) taskDetails: TaskDetailsComponent;
    @ViewChild('taskDetailsModal') taskDetailsModal: DeprecatedModalComponent;

    @ViewChild('selectEmployees', { static: true }) selectEmployees: BulkSelectEmployeeDialogComponent;

    backRoute = '';
    employee: Employee;

    hasTaskPermission = false;
    taskTemplate: TaskTemplate;
    isLoading = true;
    hasAssignees = false;
    assignedToLabel: Translatable;

    currentTask: Task;

    taskToBeDeleted: string;

    tasksTable: TaskTemplateCompanyTasksTable;

    assignedEmployees: Employee[] = [];

    bulkEmployeeModalBannerCopy: Translatable;

    private titleCasePipe: TitlecasePipe = new TitlecasePipe();

    constructor(
        private route: ActivatedRoute,
        private auth: AuthService,
        private notify: NotifyService,
        private router: Router,
        private assignTasksService: AssignTaskTemplatesService,
        public analyticService: AnalyticService,
        private routingState: RoutingStateService
    ) {
        this.employee = this.auth.employee;

        this.hasTaskPermission = this.auth.isAdmin() || this.auth.can(TaskTemplate.taskPermission);
        this.taskTemplate = this.route.snapshot.data.models.get('taskTemplate');
        this.isLoading = false;

        this.tasksTable = new TaskTemplateCompanyTasksTable(this.auth, this.taskTemplate);
    }

    ngOnInit(): void {
        this.hasAssignees = this.taskTemplate.assignees.length > 0;
        this.assignedToLabel = this.getAssignedToLabel();

        // Do not go back to new or edit form
        this.backRoute = this.routingState.firstPreviousUrlNotMatching(/(\/edit|\/new)/);

        this.setBulkEmployeeModalBannerCopy();
    }

    onRowClicked(task: Task): void {
        this.taskDetails.beforeShow(task);
        this.taskDetailsModal.show();

        this.currentTask = task;
    }

    onClose(): void {
        this.taskDetailsModal.onClose();
    }

    async onTableMenuEvent(event: MenuColumnEvent): Promise<void> {
        const eventType = event.menuItem.event;
        const task = event.data as Task;

        switch (eventType) {
            case 'remind':
                this.onRemind(task);
                break;
            case 'delete':
                this.taskToBeDeleted = task.name;
                await this.onDeleteTask(task);
                break;
            case 'edit':
                this.onEditTask(task);
                break;
        }
    }

    onCreateTaskFromTemplate(): void {
        this.selectEmployees.show<Employee[]>(this.assignedEmployees).then((selected: Employee[]) => {
            if (selected === null) {
                return;
            }

            this.assignedEmployees = selected;

            this.assignTasksFromTemplate(this.assignedEmployees);
        });
    }

    onEditTaskTemplate(): void {
        this.router.navigate(['edit'], { relativeTo: this.route });
    }

    async onDeleteTaskTemplate(): Promise<void> {
        const shouldDelete = await this.confirmDeleteModal.open();

        if (!shouldDelete) {
            return;
        }

        this.taskTemplate
            .param('company', this.taskTemplate.companyId)
            .delete()
            .then(() => {
                this.notify.success('employees.tasks-template.taskTemplateDeleted');
                this.router.navigate(['../'], { relativeTo: this.route });
            })
            .catch((err) => {
                this.notify.error(err.message || 'employees.tasks-template.taskTemplateDeletedError');
                this.isLoading = false;
            });
    }

    async onRemindIncomplete(): Promise<void> {
        this.isLoading = true;

        try {
            await this.taskTemplate.sendReminders();
            this.notify.success('employees.tasks-template.reminderSent');
        } catch (e) {
            this.notify.error('employees.tasks-template.unableToSendReminder');
        } finally {
            this.isLoading = false;
        }
    }

    private assignTasksFromTemplate(assignedEmployees: Employee[]) {
        this.assignTasksService
            .onSave([this.taskTemplate], assignedEmployees)
            .then(() => {
                this.analyticService.trackEvent(AnalyticEvents.ApplyTaskTemplateEventFromTaskTemplateDetails);
                this.tasksTable.reload();
                this.isLoading = false;
            })
            .catch(() => (this.isLoading = false));
    }

    private getAssignedToLabel(): Translatable | null {
        if (this.hasAssignees) {
            return null;
        }

        if (this.taskTemplate.assigneeType === TaskTemplate.assigeeTypeManager) {
            return 'employees.tasks-template.directManager';
        }

        if (this.taskTemplate.assigneeType === TaskTemplate.assigeeTypeSubject) {
            return { key: 'employees.tasks-template.taskTypeEmployee', params: { taskType: this.getTaskTypeLabel() } };
        }

        return 'employees.tasks-template.unassigned';
    }

    private getTaskTypeLabel(): string {
        return this.taskTemplate.taskType !== 'general' ? this.titleCasePipe.transform(this.taskTemplate.taskType) : '';
    }

    private onRemind(task: Task) {
        this.isLoading = true;

        task.sendReminder()
            .then(() => {
                this.notify.success('employees.tasks-template.reminderSent');
                this.isLoading = false;
            })
            .catch((err) => {
                this.notify.error(err.message);
            });
    }

    private onEditTask(task: Task): void {
        this.router.navigate(['/employees', 'directory', task.subjectId, 'tasks', task.id, 'edit']);
    }

    private async onDeleteTask(task: Task): Promise<void> {
        const shouldDelete = await this.confirmDeleteModal.open();

        if (!shouldDelete) {
            return;
        }

        this.isLoading = true;

        task.param('company', task.companyId)
            .delete()
            .then(() => {
                this.notify.success('employees.tasks-template.taskHasBeenDeleted');
                this.tasksTable.reload();
                this.isLoading = false;
            })
            .catch((err) => {
                this.notify.error(err.message || 'employees.tasks-template.taskDeletedError');
                this.isLoading = false;
            });
    }

    private setBulkEmployeeModalBannerCopy(): void {
        if (this.taskTemplate.assigneeType == 'subject') {
            this.bulkEmployeeModalBannerCopy = 'employees.tasks-template.taskWillBeCompletedByEmployeesBelow';
        }

        if (this.taskTemplate.assigneeType == 'manager') {
            this.bulkEmployeeModalBannerCopy = 'employees.tasks-template.taskWillBeCompletedByDirectManagers';
        }

        if (!this.taskTemplate.assigneeType && this.taskTemplate.assignees.length) {
            if (this.taskTemplate.assignees.length == 1) {
                this.bulkEmployeeModalBannerCopy = {
                    key: 'employees.tasks-template.taskWillBeCompletedByName',
                    params: { fullName: this.taskTemplate.assignees[0].fullName },
                };
            } else {
                this.bulkEmployeeModalBannerCopy = {
                    key: 'employees.tasks-template.taskWillBeCompletedByNameOrOthers',
                    params: {
                        fullName: this.taskTemplate.assignees[0].fullName,
                        count: this.taskTemplate.assignees.length - 1,
                    },
                };
            }
        }
    }
}
